'use client';

import React, { useState, useMemo } from 'react';
import { ExtendedArchiMateDiagram } from '@/lib/archimate/extended-types';
import {
  generateLayeredView,
  generateDependencyGraph,
  generateDataFlowView,
  generateComponentHierarchy,
  calculateViewStatistics,
  exportView,
  type ViewData,
  type LayeredViewData,
  type DependencyGraphData,
  type DataFlowViewData,
  type ComponentHierarchyData,
} from '@/lib/archimate/view-generator-utils';
import { X, Layers, GitBranch, Workflow, Network, Download, FileJson, FileText } from 'lucide-react';
import { PanelHelpDialog, HelpIconButton } from './PanelHelpDialog';

interface ViewGeneratorPanelProps {
  diagram: ExtendedArchiMateDiagram;
  onClose: () => void;
}

export default function ViewGeneratorPanel({ diagram, onClose }: ViewGeneratorPanelProps) {
  const [viewType, setViewType] = useState<'layered' | 'dependency' | 'dataflow' | 'hierarchy'>('layered');
  const [generatedView, setGeneratedView] = useState<ViewData | null>(null);
  const [showHelp, setShowHelp] = useState(false);

  // Generate view when type changes
  const currentView = useMemo(() => {
    switch (viewType) {
      case 'layered':
        return generateLayeredView(diagram);
      case 'dependency':
        return generateDependencyGraph(diagram);
      case 'dataflow':
        return generateDataFlowView(diagram);
      case 'hierarchy':
        return generateComponentHierarchy(diagram);
    }
  }, [diagram, viewType]);

  const statistics = useMemo(() => {
    return calculateViewStatistics(diagram, currentView);
  }, [diagram, currentView]);

  const handleExportJSON = () => {
    const json = exportView(diagram, currentView, {
      includeStatistics: true,
      includeMetadata: true,
      format: 'json',
    });

    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${diagram.name}-${viewType}-view.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleExportMarkdown = () => {
    const markdown = exportView(diagram, currentView, {
      includeStatistics: true,
      includeMetadata: true,
      format: 'markdown',
    });

    const blob = new Blob([markdown], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${diagram.name}-${viewType}-view.md`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="fixed top-0 right-0 h-full w-[600px] bg-background/95 backdrop-blur border-l border-border shadow-2xl z-50 flex flex-col animate-in slide-in-from-right duration-300">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-border">
        <div className="flex items-center gap-3">
          <Network className="w-5 h-5 text-accent" />
          <h2 className="text-xl font-bold text-foreground">View Generator</h2>
          <HelpIconButton onClick={() => setShowHelp(true)} />
        </div>
        <button
          onClick={onClose}
          className="p-2 hover:bg-cream/10 rounded-lg transition-colors"
          aria-label="Close panel"
        >
          <X className="w-5 h-5 text-foreground" />
        </button>
      </div>

      {/* Help Dialog */}
      {showHelp && (
        <PanelHelpDialog
          panelKey="view-generator"
          onClose={() => setShowHelp(false)}
        />
      )}

      {/* View Type Selector */}
      <div className="px-6 py-4 border-b border-border">
        <label className="block text-sm font-medium text-foreground-secondary mb-3">View Type</label>
        <div className="grid grid-cols-2 gap-2">
          <button
            onClick={() => setViewType('layered')}
            className={`flex items-center gap-2 px-4 py-3 rounded-lg border-2 transition-all ${
              viewType === 'layered'
                ? 'border-accent bg-accent-muted text-accent'
                : 'border-border text-foreground-secondary hover:border-border/40'
            }`}
          >
            <Layers className="w-4 h-4" />
            <span className="font-medium">Layered</span>
          </button>

          <button
            onClick={() => setViewType('dependency')}
            className={`flex items-center gap-2 px-4 py-3 rounded-lg border-2 transition-all ${
              viewType === 'dependency'
                ? 'border-accent bg-accent-muted text-accent'
                : 'border-border text-foreground-secondary hover:border-border/40'
            }`}
          >
            <GitBranch className="w-4 h-4" />
            <span className="font-medium">Dependency</span>
          </button>

          <button
            onClick={() => setViewType('dataflow')}
            className={`flex items-center gap-2 px-4 py-3 rounded-lg border-2 transition-all ${
              viewType === 'dataflow'
                ? 'border-accent bg-accent-muted text-accent'
                : 'border-border text-foreground-secondary hover:border-border/40'
            }`}
          >
            <Workflow className="w-4 h-4" />
            <span className="font-medium">Data Flow</span>
          </button>

          <button
            onClick={() => setViewType('hierarchy')}
            className={`flex items-center gap-2 px-4 py-3 rounded-lg border-2 transition-all ${
              viewType === 'hierarchy'
                ? 'border-accent bg-accent-muted text-accent'
                : 'border-border text-foreground-secondary hover:border-border/40'
            }`}
          >
            <Network className="w-4 h-4" />
            <span className="font-medium">Hierarchy</span>
          </button>
        </div>
      </div>

      {/* Statistics */}
      <div className="px-6 py-4 border-b border-border">
        <h3 className="text-sm font-medium text-foreground-secondary mb-3">View Statistics</h3>
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-cream/5 rounded-lg px-4 py-3">
            <div className="text-xs text-foreground-tertiary mb-1">Elements</div>
            <div className="text-2xl font-bold text-foreground">{statistics.elementCount}</div>
          </div>

          <div className="bg-cream/5 rounded-lg px-4 py-3">
            <div className="text-xs text-foreground-tertiary mb-1">Relationships</div>
            <div className="text-2xl font-bold text-foreground">{statistics.relationshipCount}</div>
          </div>

          <div className="bg-cream/5 rounded-lg px-4 py-3">
            <div className="text-xs text-foreground-tertiary mb-1">Complexity</div>
            <div className="text-2xl font-bold text-foreground">{statistics.complexity}</div>
          </div>

          <div className="bg-cream/5 rounded-lg px-4 py-3">
            <div className="text-xs text-foreground-tertiary mb-1">Depth</div>
            <div className="text-2xl font-bold text-foreground">{statistics.depth}</div>
          </div>
        </div>
      </div>

      {/* View Details */}
      <div className="flex-1 overflow-auto px-6 py-4">
        {currentView.type === 'layered' && <LayeredViewDetails view={currentView as LayeredViewData} />}
        {currentView.type === 'dependency' && <DependencyViewDetails view={currentView as DependencyGraphData} />}
        {currentView.type === 'dataflow' && <DataFlowViewDetails view={currentView as DataFlowViewData} />}
        {currentView.type === 'hierarchy' && <HierarchyViewDetails view={currentView as ComponentHierarchyData} />}
      </div>

      {/* Export Actions */}
      <div className="px-6 py-4 border-t border-border space-y-2">
        <button
          onClick={handleExportJSON}
          className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-accent hover:bg-accent-hover text-accent-foreground rounded-lg font-medium transition-colors"
        >
          <FileJson className="w-4 h-4" />
          Export as JSON
        </button>

        <button
          onClick={handleExportMarkdown}
          className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-cream/10 hover:bg-cream/20 text-foreground rounded-lg font-medium transition-colors"
        >
          <FileText className="w-4 h-4" />
          Export as Markdown
        </button>
      </div>
    </div>
  );
}

// ============================================================================
// VIEW DETAIL COMPONENTS
// ============================================================================

function LayeredViewDetails({ view }: { view: LayeredViewData }) {
  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-sm font-medium text-foreground-secondary mb-2">Layers</h3>
        <div className="space-y-2">
          {view.layers.map((layer) => (
            <div key={layer.layer} className="bg-cream/5 rounded-lg p-3">
              <div className="flex items-center justify-between mb-2">
                <span className="font-medium text-foreground capitalize">{layer.layer}</span>
                <span className="text-xs text-foreground-tertiary">{layer.elementCount} elements</span>
              </div>
              <div className="space-y-1">
                {layer.elements.slice(0, 5).map((element) => (
                  <div key={element.id} className="text-sm text-foreground-secondary truncate">
                    • {element.name}
                  </div>
                ))}
                {layer.elements.length > 5 && (
                  <div className="text-xs text-foreground-tertiary italic">
                    +{layer.elements.length - 5} more
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-accent-muted border border-border rounded-lg p-4">
        <div className="text-sm text-foreground-secondary mb-1">Cross-Layer Connections</div>
        <div className="text-2xl font-bold text-accent">{view.crossLayerConnections}</div>
        <div className="text-xs text-foreground-tertiary mt-1">
          Relationships connecting different layers
        </div>
      </div>
    </div>
  );
}

function DependencyViewDetails({ view }: { view: DependencyGraphData }) {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <div className="bg-cream/5 rounded-lg p-3">
          <div className="text-xs text-foreground-tertiary mb-1">Root Elements</div>
          <div className="text-xl font-bold text-foreground">{view.roots.length}</div>
          <div className="text-xs text-foreground-tertiary mt-1">No dependencies</div>
        </div>

        <div className="bg-cream/5 rounded-lg p-3">
          <div className="text-xs text-foreground-tertiary mb-1">Leaf Elements</div>
          <div className="text-xl font-bold text-foreground">{view.leaves.length}</div>
          <div className="text-xs text-foreground-tertiary mt-1">No dependents</div>
        </div>
      </div>

      {view.cycles.length > 0 && (
        <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-2 h-2 bg-red-500 rounded-full"></div>
            <div className="text-sm font-medium text-red-400">Circular Dependencies Detected</div>
          </div>
          <div className="text-xs text-foreground-tertiary mb-2">{view.cycles.length} cycles found</div>
          <div className="space-y-1">
            {view.cycles.slice(0, 3).map((cycle, idx) => (
              <div key={idx} className="text-xs text-foreground-secondary truncate">
                {cycle.join(' → ')}
              </div>
            ))}
            {view.cycles.length > 3 && (
              <div className="text-xs text-foreground-tertiary italic">+{view.cycles.length - 3} more</div>
            )}
          </div>
        </div>
      )}

      <div>
        <h3 className="text-sm font-medium text-foreground-secondary mb-2">Dependency Levels</h3>
        <div className="space-y-2">
          {Array.from({ length: view.maxDepth + 1 }, (_, level) => {
            const nodesAtLevel = view.nodes.filter((n) => n.level === level);
            return (
              <div key={level} className="bg-cream/5 rounded-lg p-3">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium text-foreground">Level {level}</span>
                  <span className="text-xs text-foreground-tertiary">{nodesAtLevel.length} elements</span>
                </div>
                <div className="space-y-1">
                  {nodesAtLevel.slice(0, 3).map((node) => (
                    <div key={node.element.id} className="text-sm text-foreground-secondary truncate">
                      • {node.element.name}
                    </div>
                  ))}
                  {nodesAtLevel.length > 3 && (
                    <div className="text-xs text-foreground-tertiary italic">
                      +{nodesAtLevel.length - 3} more
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function DataFlowViewDetails({ view }: { view: DataFlowViewData }) {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <div className="bg-cream/5 rounded-lg p-3">
          <div className="text-xs text-foreground-tertiary mb-1">Data Sources</div>
          <div className="text-xl font-bold text-foreground">{view.sources.length}</div>
          <div className="text-xs text-foreground-tertiary mt-1">Produce data</div>
        </div>

        <div className="bg-cream/5 rounded-lg p-3">
          <div className="text-xs text-foreground-tertiary mb-1">Data Sinks</div>
          <div className="text-xl font-bold text-foreground">{view.sinks.length}</div>
          <div className="text-xs text-foreground-tertiary mt-1">Consume data</div>
        </div>
      </div>

      <div>
        <h3 className="text-sm font-medium text-foreground-secondary mb-2">Data Objects</h3>
        <div className="space-y-1">
          {view.dataObjects.length === 0 && (
            <div className="text-sm text-foreground-tertiary italic">No data objects in diagram</div>
          )}
          {view.dataObjects.slice(0, 5).map((obj) => (
            <div key={obj.id} className="bg-cream/5 rounded-lg p-2 text-sm text-foreground-secondary">
              {obj.name} <span className="text-xs text-foreground-tertiary">({obj.type})</span>
            </div>
          ))}
          {view.dataObjects.length > 5 && (
            <div className="text-xs text-foreground-tertiary italic">
              +{view.dataObjects.length - 5} more
            </div>
          )}
        </div>
      </div>

      <div>
        <h3 className="text-sm font-medium text-foreground-secondary mb-2">Flow Summary</h3>
        <div className="space-y-2">
          <div className="bg-cream/5 rounded-lg p-3">
            <div className="text-xs text-foreground-tertiary mb-1">Access Relationships</div>
            <div className="text-xl font-bold text-foreground">
              {view.edges.filter((e) => e.flowType === 'access').length}
            </div>
          </div>
          <div className="bg-cream/5 rounded-lg p-3">
            <div className="text-xs text-foreground-tertiary mb-1">Flow Relationships</div>
            <div className="text-xl font-bold text-foreground">
              {view.edges.filter((e) => e.flowType === 'flow').length}
            </div>
          </div>
          <div className="bg-cream/5 rounded-lg p-3">
            <div className="text-xs text-foreground-tertiary mb-1">Triggering Relationships</div>
            <div className="text-xl font-bold text-foreground">
              {view.edges.filter((e) => e.flowType === 'triggering').length}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function HierarchyViewDetails({ view }: { view: ComponentHierarchyData }) {
  return (
    <div className="space-y-4">
      <div className="bg-cream/5 rounded-lg p-3">
        <div className="text-xs text-foreground-tertiary mb-1">Root Components</div>
        <div className="text-xl font-bold text-foreground">{view.roots.length}</div>
        <div className="text-xs text-foreground-tertiary mt-1">Top-level elements</div>
      </div>

      <div>
        <h3 className="text-sm font-medium text-foreground-secondary mb-2">Hierarchy Levels</h3>
        <div className="space-y-2">
          {Array.from({ length: view.maxDepth + 1 }, (_, level) => {
            const nodesAtLevel = view.nodes.filter((n) => n.level === level);
            return (
              <div key={level} className="bg-cream/5 rounded-lg p-3">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium text-foreground">Level {level}</span>
                  <span className="text-xs text-foreground-tertiary">{nodesAtLevel.length} elements</span>
                </div>
                <div className="space-y-1">
                  {nodesAtLevel.slice(0, 3).map((node) => (
                    <div key={node.element.id} className="text-sm text-foreground-secondary">
                      <span className="truncate">• {node.element.name}</span>
                      {node.children.length > 0 && (
                        <span className="text-xs text-foreground-tertiary ml-2">
                          ({node.children.length} children)
                        </span>
                      )}
                    </div>
                  ))}
                  {nodesAtLevel.length > 3 && (
                    <div className="text-xs text-foreground-tertiary italic">
                      +{nodesAtLevel.length - 3} more
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
